import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import {
  changePasswordStart,
  changePasswordSuccess,
  changePasswordFailure,
} from "../../store/slices/authSlice";
import toast from "react-hot-toast";
import { RootState } from "../../store";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { authService } from "../../services/authService";

const ChangePassword: React.FC = () => {
  const dispatch = useDispatch();
  const { isLoading, passwordChangeError } = useSelector(
    (state: RootState) => state.auth
  );

  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const togglePasswordVisibility = (field: "current" | "new" | "confirm") => {
    setShowPasswords({
      ...showPasswords,
      [field]: !showPasswords[field],
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.newPassword !== formData.confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }

    dispatch(changePasswordStart());

    try {
      await authService.changePassword({
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
      });

      dispatch(changePasswordSuccess());
      toast.success("Password changed successfully");
      setFormData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (error) {
      dispatch(
        changePasswordFailure(
          error instanceof Error ? error.message : "Failed to change password"
        )
      );
      toast.error("Failed to change password");
    }
  };

  return (
    <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-md p-6">
      <h3 className="text-xl font-semibold mb-4 text-secondary-900 dark:text-white">
        Change Password
      </h3>

      {passwordChangeError && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
          {passwordChangeError}
        </div>
      )}

      <motion.form
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        onSubmit={handleSubmit}
        className="space-y-6"
      >
        <div>
          <label
            htmlFor="currentPassword"
            className="block text-sm font-medium text-secondary-700 dark:text-secondary-300"
          >
            Current Password
          </label>
          <div className="mt-1 relative">
            <input
              id="currentPassword"
              name="currentPassword"
              type={showPasswords.current ? "text" : "password"}
              required
              value={formData.currentPassword}
              onChange={handleInputChange}
              className="input-field pr-10"
              placeholder="Enter your current password"
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
              onClick={() => togglePasswordVisibility("current")}
            >
              {showPasswords.current ? (
                <EyeSlashIcon className="h-5 w-5 text-secondary-400" />
              ) : (
                <EyeIcon className="h-5 w-5 text-secondary-400" />
              )}
            </button>
          </div>
        </div>

        <div>
          <label
            htmlFor="newPassword"
            className="block text-sm font-medium text-secondary-700 dark:text-secondary-300"
          >
            New Password
          </label>
          <div className="mt-1 relative">
            <input
              id="newPassword"
              name="newPassword"
              type={showPasswords.new ? "text" : "password"}
              required
              value={formData.newPassword}
              onChange={handleInputChange}
              className="input-field pr-10"
              placeholder="Enter your new password"
              minLength={6}
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
              onClick={() => togglePasswordVisibility("new")}
            >
              {showPasswords.new ? (
                <EyeSlashIcon className="h-5 w-5 text-secondary-400" />
              ) : (
                <EyeIcon className="h-5 w-5 text-secondary-400" />
              )}
            </button>
          </div>
        </div>

        <div>
          <label
            htmlFor="confirmPassword"
            className="block text-sm font-medium text-secondary-700 dark:text-secondary-300"
          >
            Confirm New Password
          </label>
          <div className="mt-1 relative">
            <input
              id="confirmPassword"
              name="confirmPassword"
              type={showPasswords.confirm ? "text" : "password"}
              required
              value={formData.confirmPassword}
              onChange={handleInputChange}
              className="input-field pr-10"
              placeholder="Confirm your new password"
              minLength={6}
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
              onClick={() => togglePasswordVisibility("confirm")}
            >
              {showPasswords.confirm ? (
                <EyeSlashIcon className="h-5 w-5 text-secondary-400" />
              ) : (
                <EyeIcon className="h-5 w-5 text-secondary-400" />
              )}
            </button>
          </div>
        </div>

        <div>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type="submit"
            disabled={isLoading}
            className="w-full btn-primary"
          >
            {isLoading ? "Changing..." : "Change Password"}
          </motion.button>
        </div>
      </motion.form>
    </div>
  );
};

export default ChangePassword;
